/**
 * Message template POJO interfaces.
 *
 * A `TemplateSet` is a tone-flavored set of SMS message templates.
 * Tokens in bodies are either primitives (`_*`, auto-derived — see
 * `form/lang/primitive.ts`) or declared user fields. User-field options may
 * also name primitives; those options resolve through the current context.
 */

import type { PrimitiveName } from '@/modules/form/lang';

/**
 * Reserved sentinel value usable inside a `user_*` field's `options` array.
 * When present, the UI exposes an "Other..." chip that reveals a free-text input.
 */
export const OTHER_OPTION = 'OTHER';

/**
 * The field whose answer becomes the leg's allowed detail, in either dialect: a
 * UUID80-shaped id code looks up, padded with underscores to 14 characters.
 */
export const ALLOWED_DETAIL_FIELD_ID = 'allowedDetail_';

/**
 * Names of all primitives, derived from the definition lists in
 * `form/lang/primitive.ts`.
 */
export type { PrimitiveName };

export interface TemplateField {
  /**
   * Identifier-shaped user field name. Cannot start with `_` and cannot be
   * `OTHER`.
   */
  readonly id: string;
  readonly label: string;
  readonly help?: string;
  /**
   * Pick strings, which may embed braced primitives such as
   * `{_profileName}`. The bare `OTHER_OPTION` sentinel reveals a free-text
   * input.
   */
  readonly options?: readonly string[];
  /**
   * Singleton-mode-only options, offered before `options`. Values may embed
   * primitives such as `{_recipientInformalName}`.
   */
  readonly optionsSingletonMode?: readonly string[];
  /**
   * Group-text-only options, offered before `options` — the mirror of
   * `optionsSingletonMode`, for text that only makes sense when the message
   * reaches several people at once. A field offering only these is vestigial
   * in a direct text, exactly as a singleton-only one is in a group text.
   */
  readonly optionsGroupMode?: readonly string[];
  /**
   * True for a field the end user may leave unset — a two-option select of
   * the empty string and a piece of text. An optional field never blocks
   * completeness, and its chosen option composes verbatim (no trim), so the
   * leading space in an optional like `" {_recipientInformalName}"` survives
   * into the sent message.
   */
  readonly optional?: boolean;
  /**
   * Optional max length for the free-text ("Other...") entry. Set by the
   * topic_garden inline dialect from a select's nested `input: { maxlength }`;
   * classic templates never set it. Applied to the compose free-text box.
   */
  readonly maxLength?: number;
  /**
   * Optional label for the free-text ("Other...") chip. Set by the inline
   * dialect from a select's nested `input: { label }`; defaults to "Other...".
   */
  readonly otherLabel?: string;
}

/**
 * Whether a share starts a topic travelling or passes on one that already is:
 * a `fresh` share is the sender's own first word about the topic, a `reshare`
 * hands on what reached them. Both compose the `intro` intent — the difference
 * is what the message has to say, so a form slot may be restricted to one of
 * them (its `forSharingModes`) the way it may be restricted to an addressing mode.
 */
export type SharingMode = 'fresh' | 'reshare';

/** A slot's `forSharingModes`: either sharing mode, or both. */
export type SharingModeGate = 'all' | SharingMode;

export type MessageIntent = 'intro' | 'reminder' | 'update';

/**
 * Per-intent scaffolding around a `wrappedBody`. The resolved true body is
 * `prefix + wrappedBody + suffix`. Declared once per file (keyed by intent) and
 * shared across every tone. prefix/suffix may embed primitives (e.g.
 * `{_asciiLogo}`, `{_topicUrl}`).
 */
export interface BodyWrap {
  readonly intent: MessageIntent;
  readonly prefix: string;
  readonly suffix: string;
}

export interface Template {
  readonly intent: MessageIntent;
  /**
   * The resolved true body. When the source used `wrappedBody`, this is
   * `wrap.prefix + wrappedBody + wrap.suffix`; when it used a literal `body`,
   * this is that body verbatim. Always present after parsing, so token
   * composition and validation operate on it uniformly.
   */
  readonly body: string;
  /**
   * The editable middle, present iff the source template used `wrappedBody`
   * (i.e. did not supply a literal `body`). Drives the default (non-master)
   * editor; `undefined` for a master-authored custom `body`.
   */
  readonly wrappedBody?: string;
  /** This intent's prefix/suffix, copied in for the default editor. */
  readonly wrap?: BodyWrap;
  readonly fields: readonly TemplateField[];
}

export interface TemplateSet {
  readonly id: string;
  readonly name: string;
  readonly icon?: string;
  readonly templates: readonly Template[];
}

/** User free-text values, keyed by user field id. */
export type FieldValueMap = Readonly<Record<string, string>>;

/** All primitives, pre-rendered for the current action+recipients+now. */
export type ContextVars = Readonly<Record<PrimitiveName, string>>;
